import { Component, EventEmitter, Input, OnInit, Output, SimpleChange, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { SelectOption } from '../../../../shared/types/select-option.interface';
import { BudgetTimeframesType } from '../../../../shared/types/budget.interface';
import { formatDate } from '@angular/common';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { IconHeaderModalService } from '../../../../shared/services/modal-with-icon-header.service';
import { ModalContext } from '../../../../shared/components/modal-with-icon-header/modal-with-icon-header.component';
import { Configuration } from '../../../../app.constants';

@Component({
  selector: 'budget-drawer-details-form',
  templateUrl: './budget-details-form.component.html',
  styleUrls: ['./budget-details-form.component.scss']
})
export class BudgetDetailsFormComponent implements OnInit {
  @Input() ownerOptions: SelectOption[] = [];
  @Input() formData: UntypedFormGroup;
  @Input() companyCurrency: { symbol: string; code: string };
  @Input() isForecastEnabled: boolean = false;
  @Input() budgetId: number;
  @Input() companyId: number;
  @Input() selectedFieldIds: number[] = [];
  @Input() selectedFieldNames: string[] = [];
  @Input() defaultSelectedFormulaIds: number[] = [];
  @Input() defaultSelectedFormulaNames: string[] = [];
  @Input() managePageFieldsKeyed: { [key: string]: any } = {};
  @Input() isEditMode: boolean = false;
  @Input() isFAB: boolean = false;
  @Input() canDisableForecast: boolean = true;

  @Output() handleOwnerChange = new EventEmitter<number>();
  @Output() handleTimeframeChange = new EventEmitter<string>();
  @Output() saveCAB = new EventEmitter<number[]>();
  @Output() startDateChanged = new EventEmitter<Date>();
  @Output() forecastEnabledChanged = new EventEmitter<boolean>();
  @Output() cabFormulaChanged = new EventEmitter<{ ids: number[]}>();
  @Output() syncUnsavedChangesFlag = new EventEmitter<boolean>();
  @Output() smartCellChanged = new EventEmitter<string>();


  @ViewChild('captchaTemplate') captchaTemplate!: TemplateRef<any>;

  private destroy$ = new Subject<void>();
  isLoading = false;

  forecastTooltip = 'Enabling Forecast helps you track the budget in the forecast column. It keeps the original budget for reference.';
  smartCellTooltip = 'Your selection here sets the basis for the Available Budget calculation and can\'t be changed later.';
  disabledForecastTooltip = 'Used in the Available Budget calculation and can\'t be disabled';
  nonHighlightedFormulaItem: string[] = [];
  formulaCheckboxOptions: { label: string; value: number; selected: boolean; disabled: boolean }[] = [];
  selectedFormulaIds: number[] = [];
  selectedItems: string[] = [];
  selectedSmartCellOption = new FormControl('budget');


  startDateFormatted = '';
  endDateFormatted = '';
  skipForecastChange = false;

  timeframeTypes = [
    { id: BudgetTimeframesType.Month, title: 'Monthly' },
    { id: BudgetTimeframesType.Quarter, title: 'Quarterly' }
  ];

  prevStateInitialized = false;
  prevState: any;
  learnMoreHelpLink: string = 'https://help.planful.com/planful-for-marketing/docs/custom-available-budget';


  currencyMaskOptions = { decimal: '.', precision: 2, align: 'right', allowNegative: false, prefix: '' };

  modalData: ModalContext = {
    title: this.configuration.forecastDescriptions.disableTitle,
    content: this.configuration.forecastDescriptions.disableContent,
    icon: {
      prefix: 'fas',
      name: 'exclamation-triangle'
    },
    buttons: [
      { text: 'Disable', color: 'warn', disabled: true }
    ]
  };

  captchaInput: string = '';
  redHeader: boolean = true;
  forecastSubscribed = false;
  hasHandledInitialForecastChange = false;
  isForecastSmartCell = false;

  constructor(
    private modalService: IconHeaderModalService,
    private readonly configuration: Configuration
  ) {}

  ngOnInit() {
    this.initializeFormulaSelection();
    this.syncUnsavedChangesFlag.emit(false);
    this.subscribeToAmountChanges();

    const forecastId = this.managePageFieldsKeyed['forecast']?.display_order;
    const anchorId = this.selectedFieldIds?.[0];

    const isForecastAnchor = forecastId != null && anchorId === forecastId;
    const smartCellValue = isForecastAnchor ? 'forecast' : 'budget';

    if(smartCellValue === 'forecast') {
      this.selectedSmartCellOption.setValue(smartCellValue, { emitEvent: false });
      this.smartCellSettings(false);
    }

    this.prevState = this.formDataToState();
    if (this.isEditMode) {
      this.selectedSmartCellOption.disable({ emitEvent: false });
    } else {
      this.selectedSmartCellOption.enable({ emitEvent: false });
    }

    this.selectedSmartCellOption.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => {
        this.handleSmartCellChange(value);
    });
  }


  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.forecastSubscribed = false;
    this.hasHandledInitialForecastChange = false;
  }

  ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    if (!this.prevStateInitialized && this.formData && this.selectedFieldIds?.length) {
      this.prevState = this.formDataToState();
      this.prevStateInitialized = true;
    }

    if (changes.formData && this.formData) {
      if (this.formData.controls.startDate) {
        this.formData.controls.startDate.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe(date => this.updateTimestampDates(date));
        this.updateTimestampDates(this.formData.controls.startDate.value);
      }

      if (this.formData.controls.forecastEnabled && !this.forecastSubscribed) {
        this.handleForecastToggleChange();
      }
    }

  }

  onCaptchaInputChange() {
    this.modalData.buttons[0].disabled = this.captchaInput !== 'DISABLE';
  }

  private handleForecastToggleChange(): void {
    const forecastControl = this.formData?.get('forecastEnabled');
    if (!forecastControl) return;
    this.forecastSubscribed = true;

    forecastControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(enabled => {
        
        if (!this.hasHandledInitialForecastChange) {
          this.hasHandledInitialForecastChange = true;
          if (enabled === this.isForecastEnabled) return;
        }

        if (this.skipForecastChange) {
          this.skipForecastChange = false;
          return;
        }

        if (this.budgetId) {
          if (!enabled) {
            this.captchaInput = '';
            this.modalService.openInitialUpdatePopup(
              this.modalData,
              this.captchaTemplate,
              this.redHeader,
              'modal-with-large-content'
            ).subscribe(result => {
              if (result) {
                this.selectedFieldIds = this.defaultSelectedFormulaIds;
                this.selectedFormulaIds = this.defaultSelectedFormulaIds.slice(1);
                this.selectedItems = this.defaultSelectedFormulaNames;
                this.selectedFieldNames = this.defaultSelectedFormulaNames;
                this.forecastEnabledChanged.emit(false);
                this.isForecastEnabled = false;
                this.resetForecast();
              } else {
                this.skipForecastChange = true;
                forecastControl.patchValue(true);
              }
            });
          } else {
            this.selectedFieldIds = this.defaultSelectedFormulaIds;
            this.selectedFormulaIds = this.defaultSelectedFormulaIds.slice(1);
            this.selectedItems = this.defaultSelectedFormulaNames;
            this.selectedFieldNames = this.defaultSelectedFormulaNames;
            this.forecastEnabledChanged.emit(enabled);
            this.isForecastEnabled = enabled;
            this.resetForecast();
          }
        } else {
          this.selectedFieldIds = this.defaultSelectedFormulaIds;
          this.selectedItems = this.defaultSelectedFormulaNames;
          this.forecastEnabledChanged.emit(enabled);
          this.isForecastEnabled = enabled;
          this.resetForecast();
        }
      });
  }

  resetForecast(){
    this.selectedSmartCellOption.setValue('budget');
    this.updateUnsavedChangesFlag();
    this.initializeFormulaSelection();
    this.saveSelectedFormula();
  }

  smartCellSettings(checkForChanges: boolean = true): void {
    this.nonHighlightedFormulaItem = [this.managePageFieldsKeyed['forecast'].name, this.managePageFieldsKeyed['actual'].name, this.managePageFieldsKeyed['available'].name];
      const updatedDefaultItems =
      [
          { label: this.managePageFieldsKeyed['remaining_committed'].name, value: this.managePageFieldsKeyed['remaining_committed'].display_order },
          { label: this.managePageFieldsKeyed['remaining_planned'].name, value: this.managePageFieldsKeyed['remaining_planned'].display_order }
        ];
      this.formulaCheckboxOptions = updatedDefaultItems.map(item => ({
        ...item,
        selected: this.selectedFieldIds.includes(item.value),
        disabled: false
      }));
      if(!this.selectedItems.includes(this.managePageFieldsKeyed['forecast'].name)) {
        this.selectedItems.unshift(this.managePageFieldsKeyed['forecast'].name);
      }
      
      this.updateCABDependencies();
      this.saveSelectedFormula(checkForChanges);
  }

  private handleSmartCellChange(value: string): void {
    if(value === 'forecast') {
      this.selectedFormulaIds = this.defaultSelectedFormulaIds.slice(1);
      this.selectedFieldNames = this.defaultSelectedFormulaNames.slice(1);
      this.selectedItems = this.defaultSelectedFormulaNames.slice(1);
      this.selectedFieldNames.unshift(this.managePageFieldsKeyed['forecast'].name);
      this.selectedFieldIds = this.defaultSelectedFormulaIds.slice(1);
      this.selectedFieldIds.unshift(this.managePageFieldsKeyed['forecast'].display_order);
      this.smartCellSettings();
    }
    else {
      this.selectedFieldIds = this.defaultSelectedFormulaIds;
      this.selectedFieldNames = this.defaultSelectedFormulaNames;
      this.selectedItems = this.defaultSelectedFormulaNames;
      this.initializeFormulaSelection();
      this.saveSelectedFormula();
    }
    this.smartCellChanged.emit(value);
    this.updateUnsavedChangesFlag();
  }

  private formDataToState(): any {
    const raw = this.formData.getRawValue();
    return {
      name: raw.name?.trim(),
      ownerId: raw.ownerId,
      amount: raw.amount,
      forecastEnabled: raw.forecastEnabled,
      startDate: raw.startDate,
      timeframeType: raw.timeframeType,
      formulaIds: [...this.selectedFormulaIds].sort(),
    };
  }

  public hasUnsavedChanges(): boolean {
    const currentState = this.formDataToState();
    return JSON.stringify(currentState) !== JSON.stringify(this.prevState);
  }

  private updateUnsavedChangesFlag(): void {
    const isDirty = this.hasUnsavedChanges();
    this.syncUnsavedChangesFlag.emit(isDirty);
  }

  get isForecastEnabledActual(): boolean {
    return !!this.formData?.controls.forecastEnabled?.value;
  }

  protected onHandleStartDateChange(e): void {
    this.formData.controls.startDate.setValue(e);
  }

  private updateTimestampDates(startDate: Date) {
    if (!startDate) {
      this.startDateFormatted = '';
      this.endDateFormatted = '';
      return;
    }

    const start = new Date(startDate);
    const existing = new Date(this.formData?.controls?.startDate?.value);

    if (start.getTime() !== existing.getTime()) {
      this.startDateChanged.emit(start); // Only emit if different
    }

    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 1);
    end.setDate(end.getDate() - 1);

    this.startDateFormatted = formatDate(start, 'dd MMM yyyy', 'en-US');
    this.endDateFormatted = formatDate(end, 'dd MMM yyyy', 'en-US');
    this.updateUnsavedChangesFlag();
  }

  private initializeFormulaSelection(): void {
    const useForecast = this.isForecastEnabledActual;

    const defaultItems = useForecast
      ? [
          { label: this.managePageFieldsKeyed['forecast'].name, value: this.managePageFieldsKeyed['forecast'].display_order },
          { label: this.managePageFieldsKeyed['remaining_committed'].name, value: this.managePageFieldsKeyed['remaining_committed'].display_order },
          { label: this.managePageFieldsKeyed['actual'].name, value: this.managePageFieldsKeyed['actual'].display_order },
          { label: this.managePageFieldsKeyed['remaining_planned'].name, value: this.managePageFieldsKeyed['remaining_planned'].display_order }
        ]
      : [
          { label: this.managePageFieldsKeyed['remaining_committed'].name, value: this.managePageFieldsKeyed['remaining_committed'].display_order },
          { label: this.managePageFieldsKeyed['remaining_planned'].name, value: this.managePageFieldsKeyed['remaining_planned'].display_order }
        ];

    this.nonHighlightedFormulaItem = useForecast
      ? [this.managePageFieldsKeyed['budget'].name, this.managePageFieldsKeyed['available'].name]
      : [this.managePageFieldsKeyed['budget'].name, this.managePageFieldsKeyed['actual'].name, this.managePageFieldsKeyed['available'].name];

    this.formulaCheckboxOptions = defaultItems.map(item => ({
      ...item,
      selected: this.selectedFieldIds.includes(item.value),
      disabled: false
    }));

    this.selectedFormulaIds = this.formulaCheckboxOptions.filter(i => i.selected).map(i => i.value);

    this.selectedItems = [];
    if (this.selectedFieldNames.includes(this.managePageFieldsKeyed['budget'].name)) {
      this.selectedItems.push(this.managePageFieldsKeyed['budget'].name);
    }
    if (this.selectedFieldNames.includes(this.managePageFieldsKeyed['actual'].name)) {
      this.selectedItems.push(this.managePageFieldsKeyed['actual'].name);
    }
    this.selectedItems.push(
      ...this.selectedFieldNames.filter(
        n => n !== this.managePageFieldsKeyed['budget'].name && n !== this.managePageFieldsKeyed['actual'].name
      )
    );

    this.updateCABDependencies();
    this.sortSelectedItems();
  }

  private updateCABDependencies() {
    const forecast = this.formulaCheckboxOptions.find(i => i.value === this.managePageFieldsKeyed['forecast'].display_order);
    const actualSpend = this.formulaCheckboxOptions.find(i => i.value === this.managePageFieldsKeyed['actual'].display_order);
    const committed = this.formulaCheckboxOptions.find(i => i.value === this.managePageFieldsKeyed['remaining_committed'].display_order);
    const planned = this.formulaCheckboxOptions.find(i => i.value === this.managePageFieldsKeyed['remaining_planned'].display_order);

    let isForecastChecked = this.selectedFormulaIds.includes(this.managePageFieldsKeyed['forecast'].display_order);
    let isActualChecked = this.selectedFormulaIds.includes(this.managePageFieldsKeyed['actual'].display_order);
    let isCommittedChecked = this.selectedFormulaIds.includes(this.managePageFieldsKeyed['remaining_committed'].display_order);
    let isPlannedChecked = this.selectedFormulaIds.includes(this.managePageFieldsKeyed['remaining_planned'].display_order);

    if (this.isForecastEnabledActual) {
      if(this.selectedSmartCellOption.value === 'forecast') {
        if (planned) {
        if (isPlannedChecked) {
          committed.selected = true;
          if (!this.selectedFormulaIds.includes(this.managePageFieldsKeyed['remaining_committed'].display_order)) {
            this.selectedFormulaIds.push(this.managePageFieldsKeyed['remaining_committed'].display_order);
          }
          if (!this.selectedItems.includes(this.managePageFieldsKeyed['remaining_committed'].name)) {
            this.selectedItems.push(this.managePageFieldsKeyed['remaining_committed'].name);
          }
          setTimeout(() => {
            committed.disabled = true;
          });
        } else {
          committed.disabled = false;
        }
      }
      }
      else{
      if (isCommittedChecked || isPlannedChecked) {
        if (actualSpend) {
          actualSpend.selected = true;
          actualSpend.disabled = true;
          if (!isActualChecked) {
            this.selectedFormulaIds.push(this.managePageFieldsKeyed['actual'].display_order);
            isActualChecked = true;
          }
          if (!this.selectedItems.includes(this.managePageFieldsKeyed['actual'].name)) {
            this.selectedItems.splice(1, 0, this.managePageFieldsKeyed['actual'].name);
          }
        }
        if (forecast) {
          forecast.selected = false;
          forecast.disabled = true;
          if (isForecastChecked) {
            this.selectedFormulaIds = this.selectedFormulaIds.filter(id => id !== this.managePageFieldsKeyed['forecast'].display_order);
            this.selectedItems = this.selectedItems.filter(item => item !== this.managePageFieldsKeyed['forecast'].name);
          }
          isForecastChecked = false;
        }
      } else {
        if (actualSpend) actualSpend.disabled = !isForecastChecked;
        if (forecast) forecast.disabled = !isActualChecked;
      }

      if (isActualChecked && isPlannedChecked && !isCommittedChecked && !isForecastChecked) {
        if (!this.selectedFormulaIds.includes(this.managePageFieldsKeyed['remaining_committed'].display_order)) {
            this.selectedFormulaIds.push(this.managePageFieldsKeyed['remaining_committed'].display_order);
          }
          if (!this.selectedItems.includes(this.managePageFieldsKeyed['remaining_committed'].name)) {
            this.selectedItems.push(this.managePageFieldsKeyed['remaining_committed'].name);
          }
          committed.selected = true;
          isCommittedChecked = true; 
        setTimeout(() => {
          committed.disabled = true;         
        });
      }

      committed.disabled = isPlannedChecked ? isCommittedChecked : false;
    }
    } else {
      if (planned) {
        if (isPlannedChecked) {
          committed.selected = true;
          if (!this.selectedFormulaIds.includes(this.managePageFieldsKeyed['remaining_committed'].display_order)) {
            this.selectedFormulaIds.push(this.managePageFieldsKeyed['remaining_committed'].display_order);
          }
          if (!this.selectedItems.includes(this.managePageFieldsKeyed['remaining_committed'].name)) {
            this.selectedItems.push(this.managePageFieldsKeyed['remaining_committed'].name);
          }
          setTimeout(() => {
            committed.disabled = true;
          });
        } else {
          committed.disabled = false;
        }
      }
    }
  
    this.sortSelectedItems();
  }

  onCheckboxChange(event: boolean, item: any) {
    const label = item.label;
    const value = item.value;
    item.selected = event;

    if (event) {
      if (!this.selectedFormulaIds.includes(value)) {
        this.selectedFormulaIds.push(value);
      }
      if (!this.selectedItems.includes(label)) {
        this.selectedItems.push(label);
      }
    } else {
      this.selectedFormulaIds = this.selectedFormulaIds.filter(v => v !== value);
      this.selectedItems = this.selectedItems.filter(l => l !== label);
    }

    this.updateCABDependencies();
    this.saveSelectedFormula();
  }

  private sortSelectedItems(): void {
    const labelOrder = [
      this.managePageFieldsKeyed['budget'].name,
      this.managePageFieldsKeyed['forecast'].name,
      this.managePageFieldsKeyed['actual'].name,
      this.managePageFieldsKeyed['remaining_committed'].name,
      this.managePageFieldsKeyed['remaining_planned'].name
    ];
    this.selectedItems.sort((a, b) => labelOrder.indexOf(a) - labelOrder.indexOf(b));
    this.selectedFormulaIds.sort();
  }

  private saveSelectedFormula(checkForChanges: boolean = true): void {
    const useForecast = this.isForecastEnabledActual;
    let selectedIds: number[] = [];

    const smartCellKey = this.selectedSmartCellOption.value; // 'budget' or 'forecast'

    if (useForecast) {
      if(smartCellKey === 'forecast') {
        selectedIds = [
        this.managePageFieldsKeyed['forecast'].display_order,
        this.managePageFieldsKeyed['actual'].display_order,
        ...this.selectedFormulaIds.filter(id => id !== this.managePageFieldsKeyed['forecast'].display_order && id !== this.managePageFieldsKeyed['actual'].display_order)
      ];
      }
      else{
        selectedIds = [
        this.managePageFieldsKeyed['budget'].display_order,
        ...this.selectedFormulaIds.filter(id => id !== this.managePageFieldsKeyed['budget'].display_order)
      ];
      }
    } else {
      selectedIds = [
        this.managePageFieldsKeyed['budget'].display_order,
        this.managePageFieldsKeyed['actual'].display_order,
        ...this.selectedFormulaIds.filter(id => id !== this.managePageFieldsKeyed['budget'].display_order && id !== this.managePageFieldsKeyed['actual'].display_order)
      ];
    }

    const cleanIds = [...new Set(selectedIds)].sort();
    this.cabFormulaChanged.emit({ ids: cleanIds });
    if (checkForChanges) {
      this.updateUnsavedChangesFlag();
    }
  }


  onOwnerChanged(event: MatSelectChange) {
    this.handleOwnerChange.emit(event.value);
    this.updateUnsavedChangesFlag();
  }

  onTimeframeTypeChanged(event: MatSelectChange) {
    this.handleTimeframeChange.emit(event.value);
    this.updateUnsavedChangesFlag();
  }

  subscribeToAmountChanges(): void {
    const amountControl = this.formData?.get('amount');
    if (amountControl) {
      amountControl.valueChanges
        .pipe(
          debounceTime(200),
          distinctUntilChanged()
        )
        .subscribe((newAmount: number) => {
          this.updateUnsavedChangesFlag();
        });
    }
  }
}
